import { List } from "@mui/material";
import { useMemo } from "react";

import { AUTH_ROLES } from "api/common/types/auth.types";
import {
  useIsErtTeamMemberQuery,
  useIsTrrtTeamMemberQuery,
} from "api/memberPortal/virtualTeam/virtualTeam.api";
import { useAuth } from "hooks/useAuth";

import CollapseButton from "./components/Buttons/CollapseButton";
import { StyledDrawer } from "./components/Drawer/StyledDrawer";
import SidebarCollapsible from "./components/Items/SidebarCollapsible";
import SidebarNav from "./components/Items/SidebarNav";
import SidebarSubLink from "./components/Items/SidebarSubLink";
import { sidebarConfig } from "./config/sidebar.config";
import { SIDEBAR_ITEM_TYPES } from "./config/sidebar.types";
import { canAccessItem } from "./config/sidebar.utils";

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (_state: boolean) => void;
}

const Sidebar = ({ sidebarOpen, setSidebarOpen }: SidebarProps) => {
  const toggleOpenDrawer = () => setSidebarOpen(!sidebarOpen);
  const { permissions } = useAuth();

  const isSme = permissions.role_type === AUTH_ROLES.SME;
  const { data: isErtMember } = useIsErtTeamMemberQuery(undefined, {
    skip: !isSme,
  });
  const { data: isTrrtMember } = useIsTrrtTeamMemberQuery(undefined, {
    skip: !isSme,
  });

  const visibleItems = useMemo(() => {
    return sidebarConfig.filter((item) => {
      if (!canAccessItem(permissions, item.allowedRoles, item.requiredArea))
        return false;

      if (isSme && "link" in item) {
        if (item.link === "/community/ear-reviews" && !isErtMember)
          return false;
        if (item.link === "/community/technicalqueries" && !isTrrtMember)
          return false;
      }

      return true;
    });
  }, [isSme, isErtMember, isTrrtMember, permissions]);

  return (
    <StyledDrawer
      variant="permanent"
      open={sidebarOpen}
      PaperProps={{
        sx: {
          backgroundColor: "sidebar.main",
          position: "relative",
          color: "sidebar.contrastText",
          overflow: "visible",
        },
      }}
    >
      <List dense sx={{ py: 0 }}>
        <CollapseButton expanded={sidebarOpen} onClick={toggleOpenDrawer} />

        {visibleItems.map((opt) => {
          if (opt.type === SIDEBAR_ITEM_TYPES.COLLAPSIBLE) {
            return (
              <SidebarCollapsible
                key={opt.label}
                icon={opt.icon}
                label={opt.label}
                expanded={sidebarOpen}
                onExpandSidebar={() => setSidebarOpen(true)}
              >
                {opt.children.map((child) => (
                  <SidebarSubLink
                    key={child.link}
                    to={child.link}
                    label={child.label}
                  />
                ))}
              </SidebarCollapsible>
            );
          }

          return (
            <SidebarNav
              key={opt.label}
              icon={opt.icon}
              label={opt.label}
              link={opt.link}
              expanded={sidebarOpen}
            />
          );
        })}
      </List>
    </StyledDrawer>
  );
};

export default Sidebar;
